import { Badge, Box, styled, useTheme } from '@mui/material'
import { useLocation } from '@reach/router'
import React, { useEffect, useState } from 'react'
import { authState } from '../../../state/auth-state'

import { Menu } from '@mui/icons-material'
import { navigate } from 'gatsby'

import { MODAL_WIDTH_MEDIUM } from '../../../constants/sizes'
import { CircleButton, HBox, VBox, WhiteBox } from '../../../elements/basic-elements'
import useSearchModal from '../../../hooks/useSearchModal'
import FaqIcon from '../../../images/icons/faq-icon'
import LogoutIcon from '../../../images/icons/logout-icon'
import MagGlassIcon from '../../../images/icons/mag-glass-icon'
import NewsletterIcon from '../../../images/icons/newsletter-icon'
import PodcastIcon from '../../../images/icons/podcast-icon'
import PremiumIcon from '../../../images/icons/premium-icon'
import ShopIcon from '../../../images/icons/shop-icon'
import profilePlaceholder from '../../../images/profile-placeholder.png'
import ActionSelector from '../../action-selector'
import CenteredModal from '../../modals/centered-modal'
import ConfirmationPrompt from '../../modals/confirmation-prompt'
import FullScreenModal from '../../full-screen-modal'
import IconAndLabelButton, { IconAndLabelButtonProps } from '../../icon-and-label-button'
import LogoLink from '../../logo-link'
import PartialScreenModal from '../../partial-screen-modal'
import { Avatar } from '../../user-avatar'
import NotificationBell from '../../notifications/notification-bell'
import BecomeImportantButton from '../become-important-button'
import LoadingShimmer from '../../loading-shimmer'
import { colors } from '../../../constants/colors'
import { navigateToProfile } from '../../../utilities/account'
import { openGleap } from '../../../utilities/gleap'

const HeaderMobile = () => {
  const { account, accountLoading, logout } = authState()
  const theme = useTheme()

  const image = account?.user?.profile.public.avatar
  const userName = account?.user?.profile.public.name
  const premium = account?.premium_access

  const [showMobileMenu, setShowMobileMenu] = useState(false)

  // -------------------------------------------
  // autoclose on nav
  // -------------------------------------------

  const route = useLocation()
  useEffect(() => {
    setShowMobileMenu(false)
  }, [route.pathname])

  // -------------------------------------------
  // search modal
  // -------------------------------------------

  const searchModalProps = useSearchModal()

  // -------------------------------------------
  // confirm logout modal
  // -------------------------------------------

  const [logoutModalOpen, setLogoutModalOpen] = useState(false)
  const confirmLogout = () => {
    logout()
    hideLogoutModal()
  }
  const hideLogoutModal = () => {
    setLogoutModalOpen(false)
  }

  return (
    <Box width="100%">
      <HBox width="100%" alignItems="center" gap={1}>
        {/* Left: menu + notifications */}
        <HBox flex={1} gap={1} alignItems="center">
          <CircleButton
            variant="contained"
            color="tertiary"
            disableElevation
            onClick={() => setShowMobileMenu(true)}
            aria-label="Expand Menu button"
          >
            <Menu sx={{ color: theme => theme.palette.text.primary }} />
          </CircleButton>
          {account && <NotificationBell circular dotBadge />}
        </HBox>

        {/* Center: logo */}
        <Box flexShrink={0}>
          <LogoLink />
        </Box>

        {/* Right: search + profile */}
        <HBox flex={1} gap={1} alignItems="center" justifyContent="flex-end">
          {accountLoading ? (
            <LoadingShimmer
              borderRadius="23px"
              width="100px"
              height="46px"
              customColors={
                theme.palette.mode === 'light'
                  ? { base: colors.BLACK_10, highlight: colors.WHITE }
                  : null
              }
              speed={2}
            />
          ) : (
            <>
              <CircleButton
                variant="contained"
                color="tertiary"
                disableElevation
                aria-label="Search"
                onClick={() => searchModalProps.setModalOpen(true)}
              >
                <MagGlassIcon size={18} />
              </CircleButton>
              {account ? (
                <CircleButton
                  variant="contained"
                  color="tertiary"
                  disableElevation
                  aria-label={userName ? `${userName}'s profile` : 'Profile'}
                  onClick={() => navigateToProfile(account)}
                >
                  <Badge badgeContent={premium ? <PremiumIcon /> : 0}>
                    <Avatar
                      src={image || profilePlaceholder}
                      size={46}
                      style={{ borderRadius: '50%' }}
                    />
                  </Badge>
                </CircleButton>
              ) : (
                <BecomeImportantButton />
              )}
            </>
          )}
        </HBox>
      </HBox>
      <PartialScreenModal open={showMobileMenu} onClose={() => setShowMobileMenu(false)}>
        <VBox sx={{ minHeight: '50vh' }} justifyContent="space-between">
          <VBox mt={1} gap={1} onClick={() => setShowMobileMenu(false)}>
            {account && !premium && (
              <WhiteBox p={0.25} height={64}>
                <MenuButton
                  color="secondary"
                  label="Join Us"
                  icon={<PremiumIcon />}
                  onClick={() => navigate('/register')}
                />
              </WhiteBox>
            )}
            <WhiteBox p={0.25} height={64} sx={{ mx: 'auto' }}>
              <MenuButton
                color="secondary"
                label={account ? 'Logout' : 'Login'}
                icon={account ? <LogoutIcon /> : undefined}
                sx={{ width: 'auto' }}
                onClick={() => {
                  if (account) {
                    setLogoutModalOpen(true)
                  } else {
                    navigate('/login')
                  }
                }}
              />
            </WhiteBox>
            <WhiteBox p={0.25} height={64}>
              <MenuButton
                color="secondary"
                label="Search"
                icon={<MagGlassIcon size={24} />}
                onClick={() => {
                  requestAnimationFrame(() => {
                    searchModalProps.setModalOpen(true)
                  })
                }}
              />
            </WhiteBox>
            <WhiteBox p={0.25} height={64}>
              <MenuButton
                color="secondary"
                label="Shop"
                icon={<ShopIcon size={24} />}
                component="a"
                href="https://shop.importantnotimportant.com/"
                target="_blank"
              />
            </WhiteBox>

            <WhiteBox p={0.25} height={64}>
              <MenuButton
                color="secondary"
                label="Podcast"
                icon={<PodcastIcon size={24} />}
                component={'a'}
                href="https://podcast.importantnotimportant.com/"
                target="_blank"
              />
            </WhiteBox>
            <WhiteBox p={0.25} height={64}>
              <MenuButton
                color="secondary"
                label="Get Updates"
                icon={<NewsletterIcon size={24} />}
                component={'a'}
                href="https://www.importantnotimportant.com/wcid-weekly-subscribe"
                target="_blank"
              />
            </WhiteBox>
            <WhiteBox p={0.25} height={64}>
              <MenuButton
                color="secondary"
                label="FAQ"
                icon={<FaqIcon size={24} />}
                component="a"
                href="/faq"
              />
            </WhiteBox>
            <WhiteBox p={0.25} height={64}>
              <MenuButton
                color="secondary"
                label="Get Help"
                icon={<FaqIcon size={24} />}
                onClick={() => {
                  void openGleap()
                }}
              />
            </WhiteBox>
          </VBox>
        </VBox>
      </PartialScreenModal>

      <CenteredModal open={logoutModalOpen} onClose={hideLogoutModal} positionAtBottomOnMobile>
        <ConfirmationPrompt
          onDeny={hideLogoutModal}
          onConfirm={confirmLogout}
          title={`Confirm Logout`}
          description="Are you sure you want to logout from WCID?"
          denyText="oops, no"
          confirmText="yes"
        />
      </CenteredModal>

      <FullScreenModal
        open={Boolean(searchModalProps.modalOpen)}
        size={MODAL_WIDTH_MEDIUM}
        onClose={() => searchModalProps.setModalOpen(false)}
      >
        <VBox p={2} flex={1}>
          <ActionSelector
            resultsOffset={2}
            sx={{ flex: 1 }}
            onSearch={() => searchModalProps.setModalOpen(false)}
          />
        </VBox>
      </FullScreenModal>
    </Box>
  )
}

const MenuButton = styled((props: IconAndLabelButtonProps) => (
  <IconAndLabelButton
    flexDirection="row"
    {...props}
    sx={{ height: '100%', width: '100%', px: 3, ...props.sx }}
  />
))``

export default HeaderMobile
